'use client';

import * as React from 'react';
import type { CityCode } from '@loci/city-editions';
import { useGetUnreadCountQuery } from '@loci/api';

import { GlassControl } from '@/components/atoms/GlassControl';
import { cn } from '@/components/atoms/primitives/utils';
import { GLOBAL_NAV_ACCENT_VARS } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import {
  ACCOUNT_AFFORDANCE_CONTROL,
  CityAuthAvatar,
  authedAccountAriaLabel,
} from '@/components/molecules/CityHeader/cityHeaderShared';
import { CityAccountSheetContent } from '@/components/molecules/NavMenuSheet/CityAccountSheetContent';
import { NavMenuLockup, NavMenuSheet } from '@/components/molecules/NavMenuSheet/NavMenuSheet';
import { useNotificationPanel } from '@/hooks/useNotificationPanel';
import { useCityIsStaging } from '@/lib/city-context.client';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';
import { cityHostLabel } from '@/lib/cross-host-urls';

/**
 * CityAccountMenu — the signed-in account control on the marketing-city header
 * (FRE-1389, DS 1.9.1 `.o-globalnav__avatar` → `buildAccount`).
 *
 * The avatar is an **identity** trigger with its own overlay: it opens the
 * account drawer (Notifications, Switch city, Sign out …) at every width. The
 * hamburger stays a **width** rule — `CityNavMenu` renders it for every
 * visitor — so a signed-in phone visitor keeps the sitemap's hub path. Before
 * FRE-1389 this control only linked to the app profile, and Sign out /
 * Notifications lived in the sitemap, which the DS hides ≥721px (PL-02).
 *
 * Trigger: a 36px glass pill — 28px avatar, plus the display name from 721px
 * (the DS drops the name on phones). The drawer is the shared `NavMenuSheet`
 * with `wideLayout="dropdown"` (FRE-1390): the DS 240px panel from 721px, the
 * takeover below.
 *
 * Island pattern — `'use client'`, mounted from the server-default `CityHeader`.
 */

const DEFAULT_LOCKUP_SRC = '/brand/loci-lettermark.svg';

export interface CityAccountMenuProps {
  readonly cityCode: CityCode;
  /** City display name for the drawer lockup + Switch city meta. */
  readonly cityName: string;
  readonly authUser: CityAuthUser;
  /** Path to the lockup SVG for the drawer chrome row. */
  readonly lockupSrc?: string;
  /**
   * Request-host staging flag (WEB-776), threaded by `CityHeader`. Wins over
   * the `CityStagingProvider` context so account links cannot pin to prod.
   */
  readonly isStaging?: boolean;
  /** Optional unread override for stories/tests; otherwise the live count. */
  readonly unreadCount?: number;
  /** Uncontrolled initial open — Storybook / tests. */
  readonly defaultOpen?: boolean;
  /** Extra classes on the trigger. */
  readonly className?: string;
}

export function CityAccountMenu({
  cityCode,
  cityName,
  authUser,
  lockupSrc = DEFAULT_LOCKUP_SRC,
  isStaging: isStagingProp,
  unreadCount: unreadCountProp,
  defaultOpen = false,
  className,
}: CityAccountMenuProps): React.ReactElement {
  const contextIsStaging = useCityIsStaging();
  const isStaging = isStagingProp ?? contextIsStaging;
  const [open, setOpen] = React.useState(defaultOpen);
  // When Notifications or Switch city opens another surface in the same
  // gesture as the drawer dismissing, skip returning focus to the avatar
  // (same handoff as CityNavMenu / AppNavMenu). Reset whenever it reopens.
  const [restoreFocusOnClose, setRestoreFocusOnClose] = React.useState(true);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const { openPanel } = useNotificationPanel();

  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    skip: unreadCountProp !== undefined,
  });
  const unreadCount = unreadCountProp ?? unreadData?.count ?? 0;

  const handleOpenChange = React.useCallback((next: boolean) => {
    setOpen(next);
    if (next) setRestoreFocusOnClose(true);
  }, []);

  const handleBeforeHandoff = React.useCallback(() => {
    setRestoreFocusOnClose(false);
  }, []);

  return (
    <>
      <GlassControl
        ref={triggerRef}
        shape="pill"
        size="nav"
        aria-label={authedAccountAriaLabel(authUser.displayName)}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-state={open ? 'open' : 'closed'}
        data-testid="city-header-account"
        data-auth="authed"
        onClick={() => handleOpenChange(true)}
        // `.city-account-trigger` (styles/components/nav-lockup.css) owns the
        // pill inset and the ≥721px name toggle.
        className={cn(ACCOUNT_AFFORDANCE_CONTROL, 'city-account-trigger', className)}
      >
        <CityAuthAvatar
          authUser={authUser}
          testId="city-header-account-avatar"
          className="size-7 text-micro"
        />
        <span
          aria-hidden="true"
          data-testid="city-header-account-name"
          className="city-account-trigger__name max-w-40 truncate font-display text-small font-medium text-fg-1"
        >
          {authUser.displayName}
        </span>
      </GlassControl>

      <NavMenuSheet
        open={open}
        onOpenChange={handleOpenChange}
        triggerRef={triggerRef}
        restoreFocusOnClose={restoreFocusOnClose}
        wideLayout="dropdown"
        title="Account"
        description={`Account settings, notifications and sign out for ${authUser.displayName}.`}
        closeLabel="Close account menu"
        eyebrow={`CITY · ${cityHostLabel(cityCode, { isStaging }).toUpperCase()}`}
        accentVars={GLOBAL_NAV_ACCENT_VARS.city}
        chromeStart={
          <NavMenuLockup src={lockupSrc} alt={`LOCI ${cityName} lockup`} label={cityName} />
        }
      >
        <CityAccountSheetContent
          cityCode={cityCode}
          cityName={cityName}
          authUser={authUser}
          isStaging={isStaging}
          unreadCount={unreadCount}
          onOpenNotifications={openPanel}
          onBeforeOpenNotifications={handleBeforeHandoff}
          onBeforeOpenWayfinder={handleBeforeHandoff}
        />
      </NavMenuSheet>
    </>
  );
}
